import { z } from 'zod';

/**
 * Payroll component catalog — the single source of truth for every component
 * type, which side of the journal entry it posts to, its default Chart-of-
 * Accounts code, and how it groups in the UI. The API derives DEBIT_COMPONENTS
 * and the GL mapping defaults from this; the frontend renders the entry form
 * from it. Keep this list and the CoA template (coaTemplate.ts) in sync.
 *
 * Side rule (§17.12): a component is a DEBIT (expense/asset) or a CREDIT
 * (liability payable + net pay). Employer taxes appear TWICE — once as an
 * expense (debit) and once as a payable (credit) at the same amount.
 */
export type PayrollComponentGroup =
  | 'earnings'
  | 'employer_tax'
  | 'employer_benefit'
  | 'employee_withholding'
  | 'employer_payable'
  | 'employee_deduction'
  | 'net_pay';

export interface PayrollComponentMeta {
  type: string;
  label: string;
  group: PayrollComponentGroup;
  side: 'debit' | 'credit';
  defaultCode: string; // default CoA code used to seed the per-company mapping
}

export const PAYROLL_COMPONENT_CATALOG = [
  // ── Earnings (DEBIT — expense) ───────────────────────────────
  { type: 'gross_wages_regular', label: 'Regular Wages', group: 'earnings', side: 'debit', defaultCode: '6200' },
  { type: 'gross_wages_overtime', label: 'Overtime', group: 'earnings', side: 'debit', defaultCode: '6201' },
  { type: 'gross_wages_bonus', label: 'Bonus', group: 'earnings', side: 'debit', defaultCode: '6202' },
  { type: 'gross_wages_commission', label: 'Commission', group: 'earnings', side: 'debit', defaultCode: '6200' },
  // Tips come in TWO kinds and only one of them is offset. Both are named
  // `gross_wages_*` deliberately: the extractor counts gross earnings with
  // `startsWith('gross_wages_')`, and tips ARE wages for tax purposes.
  //
  //   tips        — charged/card tips the employer collected and is paying out
  //                 in THIS payroll. Real money leaving; NOT offset.
  //   tips_cash   — cash the customer handed the employee directly. Taxed as
  //                 wages, then removed again by `tips_already_paid`, because
  //                 the employee already has it. Must equal that deduction.
  { type: 'gross_wages_tips', label: 'Tips (paid through payroll)', group: 'earnings', side: 'debit', defaultCode: '6200' },
  { type: 'gross_wages_tips_cash', label: 'Cash Tips (already received by employee)', group: 'earnings', side: 'debit', defaultCode: '6200' },
  { type: 'gross_wages_other', label: 'Other Earnings', group: 'earnings', side: 'debit', defaultCode: '6200' },

  // ── Employer taxes (DEBIT — expense) ─────────────────────────
  { type: 'employer_ss_expense', label: 'Employer Social Security (expense)', group: 'employer_tax', side: 'debit', defaultCode: '6210' },
  { type: 'employer_medicare_expense', label: 'Employer Medicare (expense)', group: 'employer_tax', side: 'debit', defaultCode: '6211' },
  { type: 'futa_expense', label: 'FUTA (expense)', group: 'employer_tax', side: 'debit', defaultCode: '6212' },
  { type: 'suta_expense', label: 'SUTA (expense)', group: 'employer_tax', side: 'debit', defaultCode: '6213' },

  // ── Employer benefits (DEBIT — expense) ──────────────────────
  { type: 'benefit_401k_employer', label: 'Employer 401(k) Match (expense)', group: 'employer_benefit', side: 'debit', defaultCode: '6215' },
  { type: 'benefit_health_employer', label: 'Employer Health Insurance (expense)', group: 'employer_benefit', side: 'debit', defaultCode: '6216' },

  // ── Employee withholdings (CREDIT — liability) ───────────────
  { type: 'fed_income_tax_withheld', label: 'Federal Income Tax Withheld', group: 'employee_withholding', side: 'credit', defaultCode: '2300' },
  { type: 'employee_ss_withheld', label: 'Employee Social Security Withheld', group: 'employee_withholding', side: 'credit', defaultCode: '2301' },
  { type: 'employee_medicare_withheld', label: 'Employee Medicare Withheld', group: 'employee_withholding', side: 'credit', defaultCode: '2302' },
  { type: 'state_income_tax_withheld', label: 'State Income Tax Withheld', group: 'employee_withholding', side: 'credit', defaultCode: '2303' },

  // ── Employer tax payables (CREDIT — liability) ───────────────
  { type: 'employer_ss_payable', label: 'Employer Social Security Payable', group: 'employer_payable', side: 'credit', defaultCode: '2310' },
  { type: 'employer_medicare_payable', label: 'Employer Medicare Payable', group: 'employer_payable', side: 'credit', defaultCode: '2311' },
  { type: 'futa_payable', label: 'FUTA Payable', group: 'employer_payable', side: 'credit', defaultCode: '2312' },
  { type: 'suta_payable', label: 'SUTA Payable', group: 'employer_payable', side: 'credit', defaultCode: '2313' },
  // The employer's own benefit cost is a liability until it reaches the plan or
  // the insurer, exactly like an employer tax. Without these the expense side
  // had nothing to credit and the run simply would not balance.
  //
  // Defaults deliberately match the employee-side accounts (2315 / 2316): both
  // halves are owed to the same provider, so one balance shows the whole debt.
  { type: 'benefit_401k_employer_payable', label: 'Employer 401(k) Match Payable', group: 'employer_payable', side: 'credit', defaultCode: '2315' },
  { type: 'benefit_health_employer_payable', label: 'Employer Health Insurance Payable', group: 'employer_payable', side: 'credit', defaultCode: '2316' },

  // ── Employee deductions (CREDIT — liability) ─────────────────
  { type: 'benefit_401k_employee', label: 'Employee 401(k) Deferral', group: 'employee_deduction', side: 'credit', defaultCode: '2315' },
  { type: 'benefit_health_employee', label: 'Employee Health Insurance', group: 'employee_deduction', side: 'credit', defaultCode: '2316' },
  // The offset to `gross_wages_tips_cash`, and equal to it by construction:
  // the cash raises gross so it is taxed, then comes straight back out because
  // the employee already has it.
  //
  // Defaults to the SAME account as the cash-tips earning on purpose, so the
  // debit and this credit cancel — no wage expense for money the business never
  // paid out (the customer did). It does NOT offset `gross_wages_tips`, which
  // is money the employer really is paying.
  { type: 'tips_already_paid', label: 'Tips Already Paid (offsets cash tips)', group: 'employee_deduction', side: 'credit', defaultCode: '6200' },
  // An advance handed to an employee earlier, coming back out of this cheque.
  // The one deduction that credits an ASSET rather than a liability: the
  // advance was booked DR Employee Advances / CR bank when it was paid, and
  // this relieves it. Wages and taxes are untouched — only net pay drops.
  //
  // Posted as its own line carrying the employee's name (see
  // PER_PERSON_COMPONENTS in lib/payroll.ts), because one advances account
  // holds everyone's balance and the name is the only thing that says whose.
  { type: 'employee_advance_repayment', label: 'Employee Advance Repayment', group: 'employee_deduction', side: 'credit', defaultCode: '1350' },

  // ── Net pay (CREDIT — bank) ──────────────────────────────────
  { type: 'net_pay_bank', label: 'Net Pay (bank/cash)', group: 'net_pay', side: 'credit', defaultCode: '1000' },
] as const satisfies readonly PayrollComponentMeta[];

export const PAYROLL_COMPONENT_TYPES = PAYROLL_COMPONENT_CATALOG.map((c) => c.type) as [
  string,
  ...string[],
];
export type PayrollComponentType = (typeof PAYROLL_COMPONENT_CATALOG)[number]['type'];

export const PAYROLL_COMPONENT_BY_TYPE: Record<string, PayrollComponentMeta> =
  Object.fromEntries(PAYROLL_COMPONENT_CATALOG.map((c) => [c.type, c]));

/** Ordered groups for rendering the entry form and the mapping settings page. */
export const PAYROLL_GROUP_ORDER: { group: PayrollComponentGroup; label: string }[] = [
  { group: 'earnings', label: 'Earnings' },
  { group: 'employer_tax', label: 'Employer Taxes (expense)' },
  { group: 'employer_benefit', label: 'Employer Benefits (expense)' },
  { group: 'employee_withholding', label: 'Employee Withholdings' },
  { group: 'employer_payable', label: 'Employer Tax Payables' },
  { group: 'employee_deduction', label: 'Employee Deductions' },
  { group: 'net_pay', label: 'Net Pay' },
];

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const nonNegMoney = z.string().regex(/^\d+(\.\d{1,4})?$/, 'must be a non-negative money value');

export const componentTypeSchema = z.enum(PAYROLL_COMPONENT_TYPES);

export const payrollComponentInputSchema = z.object({
  componentType: componentTypeSchema,
  amount: nonNegMoney,
});
export type PayrollComponentInput = z.infer<typeof payrollComponentInputSchema>;

export const payrollPaystubInputSchema = z.object({
  employeeContactId: z.string().uuid().nullable().optional(),
  employeeName: z.string().trim().min(1).max(160),
  components: z.array(payrollComponentInputSchema).min(1, 'at least one component is required'),
  /**
   * This stub's own pay date and period. Salary and hourly staff run on
   * different cycles, so one register legitimately carries several pay dates —
   * each stub becomes its own journal entry on its own date.
   *
   * Optional: when a stub does not repeat the date, the run-level value is
   * used, which is how a plain single-date register behaves.
   */
  payDate: isoDate.optional(),
  periodStart: isoDate.optional(),
  periodEnd: isoDate.optional(),
});
export type PayrollPaystubInput = z.infer<typeof payrollPaystubInputSchema>;

export const createPayrollRunInputSchema = z
  .object({
    payDate: isoDate,
    periodStart: isoDate,
    periodEnd: isoDate,
    memo: z.string().max(500).nullable().optional(),
    paystubs: z.array(payrollPaystubInputSchema).min(1, 'at least one paystub is required'),
    /**
     * Post even though a live run already exists for this pay date. Off by
     * default: re-uploading the same register is the likely mistake, and it
     * would double the payroll. A genuine second run on one date (a bonus
     * alongside regular pay) is real, so this is an override, not a ban.
     */
    allowDuplicatePayDate: z.boolean().optional(),
  })
  .refine((v) => v.periodStart <= v.periodEnd, {
    message: 'periodStart must be on or before periodEnd',
    path: ['periodEnd'],
  });
export type CreatePayrollRunInput = z.infer<typeof createPayrollRunInputSchema>;

export const voidPayrollRunInputSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type VoidPayrollRunInput = z.infer<typeof voidPayrollRunInputSchema>;

export const updatePayrollMappingsInputSchema = z.object({
  mappings: z
    .array(z.object({ componentType: componentTypeSchema, accountId: z.string().uuid() }))
    .min(1),
});
export type UpdatePayrollMappingsInput = z.infer<typeof updatePayrollMappingsInputSchema>;

// ── Owner-reimbursed employees ──────────────────────────────────────────────

/**
 * An employee whose payroll cost the OWNER pays back to the company.
 *
 * When one of this person's stubs posts, its entry also carries
 * DR receivable / CR contra for `sharePercent` of the employer cost (gross
 * wages + employer taxes + employer benefits, less the cash-tips offset). The
 * wages account keeps W-2 gross so the 941s tie; the contra nets the P&L to
 * zero; the receivable clears when the owner deposits the money.
 *
 * Matching is per stub: by contact id when both sides carry one, otherwise by
 * name, ignoring case and extra spaces. Nobody else's stub is touched.
 */
const sharePercent = z
  .string()
  .regex(/^\d{1,3}(\.\d{1,4})?$/, 'expected a percentage such as 100 or 62.5')
  .refine((v) => Number(v) > 0 && Number(v) <= 100, 'share must be above 0 and at most 100');

export const reimbursableEmployeeInputSchema = z.object({
  /** Present on an existing row; absent to add one. */
  id: z.string().uuid().optional(),
  employeeName: z.string().trim().min(1).max(160),
  employeeContactId: z.string().uuid().nullable().optional(),
  sharePercent: sharePercent.default('100'),
  /** Asset account the owner owes into (Due from Owner). */
  receivableAccountId: z.string().uuid(),
  /** Expense-type contra account that takes the credit. */
  contraAccountId: z.string().uuid(),
  isActive: z.boolean().default(true),
});
export type ReimbursableEmployeeInput = z.infer<typeof reimbursableEmployeeInputSchema>;

/** The full set for the company; rows missing from the list are removed. */
export const updateReimbursableEmployeesInputSchema = z.object({
  employees: z.array(reimbursableEmployeeInputSchema).max(50),
});
export type UpdateReimbursableEmployeesInput = z.infer<typeof updateReimbursableEmployeesInputSchema>;

export interface PayrollReimbursableEmployee {
  id: string;
  employeeName: string;
  employeeContactId: string | null;
  sharePercent: string;
  receivableAccountId: string;
  receivableAccountCode: string;
  receivableAccountName: string;
  contraAccountId: string;
  contraAccountCode: string;
  contraAccountName: string;
  isActive: boolean;
}

/**
 * Apply the owner charge-back to stubs posted BEFORE their rule existed.
 * Rewrites each stub's own entry in place (reconciled lines untouched,
 * settlement links re-pointed) and records the amount on the paystub. Dry run
 * by default; a stub already carrying the pair is skipped, so it is safe to
 * run again.
 */
export const applyOwnerReimbursementInputSchema = z
  .object({
    from: isoDate,
    to: isoDate,
    dryRun: z.boolean().default(true),
  })
  .refine((v) => v.from <= v.to, { message: 'from must be on or before to', path: ['to'] });
export type ApplyOwnerReimbursementInput = z.infer<typeof applyOwnerReimbursementInputSchema>;

export interface OwnerReimbursementPlanRow {
  paystubId: string;
  employeeName: string;
  payDate: string;
  journalEntryId: string;
  entryNumber: string;
  employerCost: string;
  sharePercent: string;
  amount: string;
}

export interface OwnerReimbursementSkippedRow {
  paystubId: string;
  employeeName: string;
  payDate: string;
  entryNumber: string | null;
  reason: string;
}

export interface ApplyOwnerReimbursementResult {
  dryRun: boolean;
  plan: OwnerReimbursementPlanRow[];
  skipped: OwnerReimbursementSkippedRow[];
  totals: { planned: number; amountTotal: string; skippedCount: number };
  applied: OwnerReimbursementPlanRow[];
  failed: (OwnerReimbursementPlanRow & { reason: string })[];
}

export const PAYROLL_RUN_STATUSES = ['posted', 'void'] as const;
export type PayrollRunStatus = (typeof PAYROLL_RUN_STATUSES)[number];

export interface PayrollComponent {
  id: string;
  componentType: string;
  amount: string;
}

export interface PayrollPaystub {
  id: string;
  employeeContactId: string | null;
  employeeName: string;
  /** This stub's own journal entry and dates. Null on runs posted before each
   *  paystub got its own entry — those hold one entry on the run instead. */
  journalEntryId: string | null;
  payDate: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  grossPay: string;
  netPay: string;
  /** Employer cost of this stub charged back to the owner; "0.00" for most. */
  ownerReimbursable: string;
  lineOrder: number;
  components: PayrollComponent[];
}

export interface PayrollRun {
  id: string;
  companyId: string;
  payDate: string;
  periodStart: string;
  periodEnd: string;
  memo: string | null;
  status: PayrollRunStatus;
  journalEntryId: string | null;
  totalGross: string;
  totalNet: string;
  voidReason: string | null;
  voidedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PayrollRunWithDetails extends PayrollRun {
  paystubs: PayrollPaystub[];
  /** Aggregated component totals across all paystubs, by component type. */
  totals: { componentType: string; amount: string }[];
}

export interface PayrollMapping {
  componentType: string;
  accountId: string;
  accountCode: string;
  accountName: string;
}

// ── AI paystub PDF extraction (§17.12) ──────────────────────────────────────

/** Allowed paystub upload mime types. PDF is the canonical format; image
 *  formats would need OCR (not wired yet) and are rejected at the route. */
export const ALLOWED_PAYSTUB_MIME = ['application/pdf'] as const;
export const MAX_PAYSTUB_BYTES = 10 * 1024 * 1024; // 10 MB — paystubs are tiny

export const extractPaystubInputSchema = z.object({
  fileName: z.string().min(1).max(255),
  mimeType: z.enum(ALLOWED_PAYSTUB_MIME),
  /** base64-encoded PDF bytes */
  content: z.string().min(1),
});
export type ExtractPaystubInput = z.infer<typeof extractPaystubInputSchema>;

export interface PaystubExtractedComponent {
  componentType: PayrollComponentType;
  amount: string;          // 2dp string, e.g. "1234.56"
  /** Whatever raw label the model saw in the PDF, e.g. "FED INC TAX". */
  rawLabel: string;
}

/** One employee's stub extracted from a paystub PDF or a payroll register. */
export interface ExtractedPaystub {
  employeeName: string | null;
  /** This stub's own pay date / period when the document repeats them per
   *  employee. Null falls back to the file-level value. */
  payDate: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  /** Sum of `gross_wages_*` reported on the stub. Display only — never balance. */
  reportedGross: string | null;
  /** Net pay reported on the stub. */
  reportedNet: string | null;
  components: PaystubExtractedComponent[];
  /**
   * Per-stub validation warnings — populated server-side. Examples:
   *   - "employer_ss_expense present but employer_ss_payable missing"
   *   - "Components don't balance: $0.34 difference"
   */
  warnings: string[];
}

/** Optional employer-side recap section a payroll register may include.
 *  Surfaces what the bank actually drafts so a future JE-per-bank-draft pass
 *  can structure the journal entries to match the bank statements. */
export interface PayrollRegisterTotals {
  reportedGross: string | null;
  reportedNet: string | null;
  reportedFederalWithheld: string | null;
  reportedStateWithheld: string | null;
  reportedEmployerTaxes: string | null;
  /** Each labeled bank draft if the register breaks them out. Empty if not. */
  bankDrafts: { label: string; amount: string }[];
}

/**
 * Result of extracting a payroll PDF. The same shape is returned whether the
 * input is a single-employee paystub (one element in `paystubs`, `registerTotals`
 * is null) or a multi-employee payroll register (one element per employee plus
 * a `registerTotals` section).
 */
export interface PayrollExtractionResult {
  /** 0.0-1.0. < 0.80 means the user MUST review every line before posting. */
  overallConfidence: number;
  employerName: string | null;
  payDate: string | null;       // ISO YYYY-MM-DD when found
  periodStart: string | null;
  periodEnd: string | null;
  paystubs: ExtractedPaystub[];
  /** Only present if the register has a totals/recap section. */
  registerTotals: PayrollRegisterTotals | null;
  /**
   * Register-level validation warnings (low confidence, sum-of-stubs vs
   * register totals mismatch, etc.). Per-stub warnings live on each
   * ExtractedPaystub.warnings.
   */
  warnings: string[];
}

/**
 * One paycheque whose net pay is still sitting in the "uncleared wage cheques"
 * holding account — the money left the payroll register but not yet the bank.
 *
 * `outstanding` is the accrued net pay less whatever settlement lines point at
 * it. A cheque only disappears from this list when a bank draft settles it
 * line-by-line, so a bulk settlement (one lump debit) leaves everyone listed —
 * correctly, since a lump debit destroys the per-person record.
 */
export interface OpenPayrollCheck {
  journalLineId: string;
  journalEntryId: string;
  entryNumber: string;
  payDate: string;
  /** The holding liability the net pay sits in — what a check paying it debits. */
  accountId: string;
  /** Null when the accrual was posted by hand rather than the payroll module. */
  employeeName: string | null;
  employeeContactId: string | null;
  memo: string | null;
  amount: string;
  settled: string;
  outstanding: string;
}

// ── Card tips breakout ──────────────────────────────────────────────────────

/**
 * Break the card-tips portion of each paystub's wages debit out onto its own
 * account. The posted entry merges every earning that maps to the wages
 * account into one debit, so the tips paid through payroll are invisible in
 * the ledger until they are split back out here.
 */
export const reclassifyPayrollTipsInputSchema = z
  .object({
    /** Pay date range, inclusive. */
    from: isoDate,
    to: isoDate,
    tipsAccountId: z.string().uuid(),
    /** True (the default) previews only; nothing is written. */
    dryRun: z.boolean().default(true),
  })
  .refine((v) => v.from <= v.to, { message: 'from must be on or before to', path: ['to'] });
export type ReclassifyPayrollTipsInput = z.infer<typeof reclassifyPayrollTipsInputSchema>;

export interface PayrollTipsPlanRow {
  paystubId: string;
  employeeName: string;
  payDate: string;
  journalEntryId: string;
  entryNumber: string;
  wagesBefore: string;
  tips: string;
  wagesAfter: string;
}

export interface PayrollTipsSkippedRow {
  paystubId: string;
  employeeName: string;
  payDate: string;
  entryNumber: string | null;
  reason: string;
}

export interface ReclassifyPayrollTipsResult {
  dryRun: boolean;
  plan: PayrollTipsPlanRow[];
  skipped: PayrollTipsSkippedRow[];
  totals: { planned: number; tipsTotal: string; skippedCount: number };
  applied: PayrollTipsPlanRow[];
  failed: (PayrollTipsPlanRow & { reason: string })[];
}
